import type { ReactNode } from 'react';
import { createContext, useContext, useEffect } from 'react';
import type { ApolloError, FetchResult } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import { useProperty, useSiteContext } from '@eti/providers';
import type {
  CancelReason,
  EventSegmentType,
  GetCancellationOptionsMutation,
  TripType,
} from '@eti/schema-types';
import { OrderState, PartOfExternalFlights } from '@eti/schema-types';

import ErrorPage from '../../status-pages/components/ErrorPage';
import {
  getJourneyType,
  getPurchasedCheckInType,
  getRyanairLocale,
  getTripSegmentsFromBounds,
  hasCheckInProduct,
  hasRyanairMarketingCarrierAndSupplier,
  isUpcoming,
} from '../utils';
import type {
  AddOnOrdersType,
  AncillaryBundlesType,
  AvailableExtraProductsType,
  BoundsType,
  CabinBaggageTripSummaryType,
  ChargeCurrencyPriceFormatType,
  CheckedBaggageSummaryType,
  CustomerDetailsType,
  DiscountPriceType,
  ExtraProductsType,
  FaqSectionType,
  FareRulesType,
  FlightInformationType,
  PaymentProductsType,
  PersonalItemTripSummaryType,
  PurchasedCheckOutType,
  RefundStatusType,
  SecondBagTripSummaryType,
  ServiceFeeType,
  TransactionBreakdownType,
  TravelerPricesType,
  TravelersType,
  TripCharacteristicsType,
  TripSegmentType,
} from '../models';
import Loading from '../../../common/loading/components/Loading';
import { useJson } from '../../../common/custom-hooks/json';
import { endpoints } from '../../../constants/routesAndApisConstants';
import { EMPTY_ADDON_CART_MUTATION } from '../../post-booking/ancillary-store/entry-page/containers/mutations';
import { isWhiteLabel } from '../../my-bookings/utils/utils';
import { ANCILLARY_AVAILABILITY, CANCELLATION_OPTIONS, FAQ, ORDER_QUERY } from './queries';
import { getUniqueTripEventTypes } from './utils';

export type OrderDetailsContextType = {
  fareRulesExtraInfo?: { fareRulesDescription?: string; systemLCCTitleAndContent?: string };
  addOnOrders: AddOnOrdersType;
  chargeCurrencyPriceFormat: ChargeCurrencyPriceFormatType;
  customerDetails: CustomerDetailsType;
  discountPrice: DiscountPriceType;
  flightInformation: FlightInformationType;
  isCheckInPurchased: boolean;
  isEligibleForCancellationCertificate: boolean;
  instalmentsTotalFee: number;
  isUpsellAllowed: boolean;
  isWhiteLabelBrand: boolean;
  orderNumber: string;
  oldOrderNumber: string;
  orderReferenceSerialized?: string | null;
  orderState: OrderState;
  cancelReason?: CancelReason | null;
  partOfExternalFlights: PartOfExternalFlights;
  paymentTypes: PaymentProductsType;
  purchaseDate: string;
  refundStatus: RefundStatusType;
  serviceFee: ServiceFeeType;
  shouldAddInstallmentFeeInTotalAmount: boolean;
  totalPrice: number;
  totalPriceIncludingAddOnOrders: number;
  totalPriceIncludingAddOnOrdersInChargeCurrency: number;
  cc2carrierBreakdown: TransactionBreakdownType;
  travelDocUrl: string;
  ancillaryBundles: AncillaryBundlesType;
  availableExtraProducts?: AvailableExtraProductsType;
  bounds: BoundsType;
  cabinBaggageTripSummary: CabinBaggageTripSummaryType;
  checkedBaggageSummary: CheckedBaggageSummaryType;
  extraProducts: ExtraProductsType;
  fareRules: FareRulesType;
  isVI: boolean;
  personalItemTripSummary: PersonalItemTripSummaryType;
  secondBagTripSummary: SecondBagTripSummaryType;
  travelers: TravelersType;
  travelerPrices: TravelerPricesType;
  tripCharacteristics: TripCharacteristicsType;
  journeyType: TripType;
  tripSegments: TripSegmentType[];
  tripId: string;
  isOrderActive: boolean;
  hasRyanairLCCSupplier: boolean;
  ryanairLocale: string | null;
  shouldShowManageTrip: boolean;
  isEligibleToBuyExtraProducts: boolean;
  isEligibleForScheduleChangeRefund: boolean;
  shouldShowUpgradeBanner: boolean;
  purchasedCheckInType: PurchasedCheckOutType | null;
  cancellationOptionsLoading: boolean;
  cancellationOptionsError?: ApolloError;
  cancellationOptionsData: GetCancellationOptionsMutation | null | undefined;
  cancellationOptionsMutation: () => Promise<FetchResult<GetCancellationOptionsMutation>>;
  eventTypes: EventSegmentType[];
  ancillaryAvailabilityLoading: boolean;
  faqSectionPerSlug?: Record<string, FaqSectionType>;
};

const OrderDetailsContext = createContext<OrderDetailsContextType | null>(null);

interface OrderDetailsProviderProps {
  children: ReactNode;
}

const OrderDetailsProvider = ({ children }: OrderDetailsProviderProps) => {
  const { p } = useProperty();
  const { agencyAccount, brand } = useSiteContext();
  const isWhiteLabelBrand = isWhiteLabel(brand.code, p('MyBookingsWelcomePage.WhiteLabelBrands'));

  const { data, loading, error } = useQuery(ORDER_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const { data: faqData } = useQuery(FAQ, {
    fetchPolicy: 'cache-first',
    skip: !p('ContactUs.FAQ.Enabled') || isWhiteLabelBrand || agencyAccount,
  });

  const faqSectionPerSlug = faqData?.faq?.sections.reduce(
    (acc: Record<string, FaqSectionType>, section: FaqSectionType) => {
      acc[section.slug] = section;
      return acc;
    },
    {},
  );

  const [emptyCart] = useMutation(EMPTY_ADDON_CART_MUTATION);

  const [
    fetchAncillaryAvailability,
    { data: ancillaryAvailabilityData, loading: ancillaryAvailabilityLoading },
  ] = useLazyQuery(ANCILLARY_AVAILABILITY, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    emptyCart()
      .then(() => {
        fetchAncillaryAvailability();
      })
      .catch((e) => {
        console.error('Error to empty the cart:', e); // eslint-disable-line no-console
      });
  }, [emptyCart, fetchAncillaryAvailability]);

  const { data: orderPageTexts, status } = useJson(endpoints.ORDERPAGE_GET_TEXTS);

  const [
    cancellationOptionsMutation,
    {
      data: cancellationOptionsData,
      loading: cancellationOptionsLoading,
      error: cancellationOptionsError,
    },
  ] = useMutation(CANCELLATION_OPTIONS);

  if (loading || status === 'PENDING') {
    return <Loading />;
  }

  const order = data?.orderNew;

  if (error || !order) {
    return <ErrorPage />;
  }

  const {
    addOnOrders,
    chargeCurrencyPriceFormat,
    customerDetails,
    discountPrice,
    flightInformation,
    isEligibleForCancellationCertificate,
    instalmentsTotalFee,
    isUpsellAllowed,
    orderNumber,
    oldOrderNumber,
    orderReferenceSerialized,
    orderState,
    cancelReason,
    partOfExternalFlights,
    paymentProducts: paymentTypes,
    purchaseDate,
    refundStatus,
    serviceFee,
    shouldAddInstallmentFeeInTotalAmount,
    totalPrice,
    totalPriceIncludingAddOnOrders,
    totalPriceIncludingAddOnOrdersInChargeCurrency,
    transactionBreakdown: cc2carrierBreakdown,
    travelDocLink: travelDocUrl,
    trips,
  } = order;

  const {
    ancillaryBundles,
    bounds,
    cabinBaggageTripSummary,
    checkedBaggageSummary,
    extraProducts,
    fareRules,
    isVI,
    personalItemTripSummary,
    secondBagTripSummary,
    travelers,
    travelerPrices,
    tripCharacteristics,
    tripId,
    type,
  } = trips[0];

  const { model: texts } = orderPageTexts || {};

  const journeyType = getJourneyType(type);
  const tripSegments = getTripSegmentsFromBounds(bounds);
  const eventTypes = getUniqueTripEventTypes(bounds);

  const isOrderActive = orderState !== OrderState.Canceled;
  const isOrderUpcoming = isUpcoming(bounds);

  const hasRyanairLCCSupplier = hasRyanairMarketingCarrierAndSupplier(
    p('IbeClient.RyanairMarketingCarriers') || [],
    p('IbeWebClient.LCCSuppliers.RequireExtraCustomerInfo') || [],
    tripSegments,
  );

  const isEligibleToBuyExtraProducts = Boolean(
    ancillaryAvailabilityData &&
      ancillaryAvailabilityData.ancillaryAvailabilityForOrder.trips[0].availableExtraProducts
        .length > 0 &&
      isOrderActive &&
      isOrderUpcoming,
  );

  const isEligibleForScheduleChangeRefund = flightInformation.some(
    (fi) => fi.isEligibleForScheduleChangeRefund,
  );

  const ryanairLocale =
    partOfExternalFlights !== PartOfExternalFlights.None
      ? getRyanairLocale(flightInformation)
      : null;
  const shouldShowManageTrip =
    isOrderActive && (isOrderUpcoming || isEligibleForScheduleChangeRefund);

  const shouldShowUpgradeBanner = Boolean(
    isUpcoming(bounds) &&
      orderState !== OrderState.Canceled &&
      partOfExternalFlights !== PartOfExternalFlights.All &&
      ancillaryAvailabilityData &&
      ancillaryAvailabilityData.ancillaryAvailabilityForOrder.trips[0].availableExtraProducts
        .length > 0,
  );

  const purchasedCheckInType = getPurchasedCheckInType(
    extraProducts,
    addOnOrders,
    ancillaryBundles,
  );

  const isCheckInPurchased = hasCheckInProduct(extraProducts, addOnOrders, ancillaryBundles);

  const value: OrderDetailsContextType = {
    fareRulesExtraInfo: texts,
    addOnOrders,
    chargeCurrencyPriceFormat,
    customerDetails,
    discountPrice,
    flightInformation,
    isCheckInPurchased,
    isEligibleForCancellationCertificate,
    instalmentsTotalFee,
    isUpsellAllowed,
    isWhiteLabelBrand,
    orderNumber,
    oldOrderNumber,
    orderReferenceSerialized,
    orderState,
    cancelReason,
    partOfExternalFlights,
    paymentTypes,
    purchaseDate,
    refundStatus,
    serviceFee,
    shouldAddInstallmentFeeInTotalAmount,
    totalPrice,
    totalPriceIncludingAddOnOrders,
    totalPriceIncludingAddOnOrdersInChargeCurrency,
    cc2carrierBreakdown,
    travelDocUrl,
    ancillaryBundles,
    bounds,
    cabinBaggageTripSummary,
    checkedBaggageSummary,
    extraProducts,
    fareRules,
    isVI,
    personalItemTripSummary,
    secondBagTripSummary,
    travelers,
    travelerPrices,
    tripCharacteristics,
    journeyType,
    tripSegments,
    tripId,
    isOrderActive,
    hasRyanairLCCSupplier,
    isEligibleToBuyExtraProducts,
    ryanairLocale,
    shouldShowManageTrip,
    isEligibleForScheduleChangeRefund,
    shouldShowUpgradeBanner,
    purchasedCheckInType,
    cancellationOptionsLoading,
    cancellationOptionsError,
    cancellationOptionsData,
    cancellationOptionsMutation,
    eventTypes,
    ancillaryAvailabilityLoading,
    availableExtraProducts:
      ancillaryAvailabilityData?.ancillaryAvailabilityForOrder?.trips[0]?.availableExtraProducts,
    faqSectionPerSlug,
  };

  return <OrderDetailsContext.Provider value={value}>{children}</OrderDetailsContext.Provider>;
};

const useOrderDetails = (): OrderDetailsContextType => {
  const context = useContext(OrderDetailsContext);

  if (isEmpty(context)) {
    throw new Error('useOrderDetails must be used within an OrderDetailsProvider');
  }

  return { ...context };
};

export default OrderDetailsProvider;
export { useOrderDetails };
